import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { CalendarDays, MapPin, Building2, Target, ListChecks, QrCode, ArrowLeft, ChevronRight } from "lucide-react";
import { api, apiError, fmtDate } from "../lib/api";
import { pendingCheckins, rememberEventCode } from "../lib/offlineQueue";
import { StatusBadge } from "../components/StatusBadge";
import { Button } from "../components/ui/button";
import { Checkbox } from "../components/ui/checkbox";
import { MissionDetailSheet } from "../components/MissionDetailSheet";
import { MissionProgress } from "../components/MissionProgress";
import { localeOf } from "../i18n";

export default function EventDetail() {
  const { id } = useParams();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [event, setEvent] = useState(null);
  const [consent, setConsent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [activeMission, setActiveMission] = useState(null);
  const [pendingCheckin, setPendingCheckin] = useState(false);
  const locale = localeOf(i18n.language);

  const load = useCallback(async () => {
    const { data } = await api.get(`/events/${id}`);
    setEvent(data);
    if (data.short_code) rememberEventCode(data.short_code, data.id);
    if (data.my_participation) setConsent(true);
    const queued = await pendingCheckins();
    setPendingCheckin(queued.some(
      (q) => q.event_id === data.id
        // Short codes are never sent to Ambassadors, so an unmapped queued check-in is
        // shown on the event they are attending and not yet confirmed on. Informational only.
        || (!q.event_id && data.my_participation && !data.my_participation.checked_in_at)));
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const doRsvp = async (status) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/events/${id}/rsvp`, { status, consent_accepted: consent });
      toast.success(status === "cancelled" ? t("events.cancelRsvp") : t(`status.${data.status}`));
      await load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  if (!event) return <p className="py-16 text-center text-sm text-muted-foreground">{t("common.loading")}</p>;

  const part = event.my_participation;
  const active = part && part.status !== "cancelled";
  const full = event.confirmed_count >= event.capacity;

  return (
    <div className="space-y-5" data-testid="event-detail-page">
      <button onClick={() => navigate(-1)} data-testid="event-back-btn"
        className="inline-flex items-center gap-1 text-sm font-semibold text-muted-foreground">
        <ArrowLeft className="h-4 w-4" />{t("common.back")}
      </button>

      <header className="vy-card overflow-hidden">
        <div className="vy-hero p-5">
          <div className="flex items-start justify-between gap-3">
            <h1 className="font-display text-2xl font-extrabold text-white" data-testid="event-title">{event.title}</h1>
            {event.status === "cancelled" && <StatusBadge status="cancelled" />}
          </div>
          <div className="mt-3 space-y-1.5 text-sm text-white/80">
            <p className="flex items-center gap-2"><CalendarDays className="h-4 w-4" />{fmtDate(event.starts_at, locale)}</p>
            {event.location && <p className="flex items-center gap-2"><MapPin className="h-4 w-4" />{event.location}</p>}
          </div>
        </div>
        <div className="space-y-4 p-5">
          {event.description && <p className="text-sm text-muted-foreground">{event.description}</p>}
          {event.campaign_name && (
            <p className="text-sm"><span className="font-semibold">{t("events.campaign")}: </span>{event.campaign_name}</p>
          )}
          {event.agenda && (
            <div><p className="flex items-center gap-2 text-sm font-bold"><ListChecks className="h-4 w-4" />{t("events.agenda")}</p>
              <p className="mt-1 whitespace-pre-line text-sm text-muted-foreground">{event.agenda}</p></div>
          )}
          {event.objectives && (
            <div><p className="flex items-center gap-2 text-sm font-bold"><Target className="h-4 w-4" />{t("events.objectives")}</p>
              <p className="mt-1 text-sm text-muted-foreground">{event.objectives}</p></div>
          )}
          {event.partner && (
            <div className="rounded-xl bg-muted p-4" data-testid="event-partner">
              <p className="flex items-center gap-2 text-sm font-bold"><Building2 className="h-4 w-4" />{t("events.partner")}</p>
              <p className="mt-1 text-sm">{event.partner.name}</p>
              {event.partner.address && <p className="text-xs text-muted-foreground">{event.partner.address}</p>}
            </div>
          )}
          <p className="text-xs font-semibold text-muted-foreground" data-testid="event-capacity">
            {event.confirmed_count}/{event.capacity} {t("events.confirmedCount")} · {event.waitlist_count} {t("events.waitlisted").toLowerCase()}
          </p>
        </div>
      </header>

      <section className="vy-card p-5" data-testid="rsvp-section">
        {active ? (
          <div className="space-y-3">
            <StatusBadge status={part.checked_in_at ? "checked_in" : part.status} testId="rsvp-status-badge" />
            {part.status === "confirmed" && !part.checked_in_at && event.status !== "cancelled" && (
              <Button data-testid="event-checkin-btn" onClick={() => navigate("/checkin")}
                className="h-14 w-full rounded-full bg-accent text-base font-bold text-accent-foreground hover:bg-accent/90">
                <QrCode className="mr-2 h-5 w-5" />{t("home.checkInNow")}
              </Button>
            )}
            {!part.checked_in_at && (
              <Button data-testid="rsvp-cancel-btn" variant="outline" disabled={busy}
                onClick={() => doRsvp("cancelled")} className="h-12 w-full rounded-full font-semibold">
                {t("events.cancelRsvp")}
              </Button>
            )}
          </div>
        ) : (
          <div className="space-y-3">
            <label className="flex gap-3 rounded-xl bg-muted p-4 text-sm">
              <Checkbox data-testid="rsvp-consent-checkbox" checked={consent}
                onCheckedChange={(v) => setConsent(!!v)} className="mt-0.5" />
              <span>{t("auth.consent")}</span>
            </label>
            {full && <p className="text-sm font-semibold text-accent" data-testid="event-full-warning">{t("events.full")}</p>}
            <Button data-testid="rsvp-confirm-btn" disabled={busy || !consent || event.status === "cancelled"}
              onClick={() => doRsvp("confirmed")}
              className="h-14 w-full rounded-full text-base font-bold active:scale-95">
              {full ? t("events.waitlisted") : t("events.rsvp")}
            </Button>
            {!consent && <p className="text-xs text-muted-foreground">{t("events.consentRequired")}</p>}
          </div>
        )}
      </section>

      <section className="space-y-3" data-testid="event-missions-section">
        <h2 className="font-display text-lg font-bold">{t("events.missions")}</h2>
        {active && event.my_progress && (
          <MissionProgress progress={event.my_progress}
            checkinState={part?.checked_in_at ? "confirmed" : pendingCheckin ? "pending_sync" : "none"} />
        )}
        {(event.missions || []).length === 0 && (
          <p className="vy-card p-4 text-sm text-muted-foreground">{t("common.none")}</p>
        )}
        {(event.missions || []).map((m) => (
          <button key={m.id} type="button" onClick={() => setActiveMission(m)}
            className="vy-card block w-full p-4 text-left transition-transform duration-150 hover:border-primary/40 active:scale-[0.99]"
            data-testid={`mission-card-${m.id}`}>
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="font-display text-base font-bold">{m.title}</p>
                <p className="text-xs font-semibold text-muted-foreground">
                  {t(`missions.${m.proof_type}`)} · {m.points} {t("common.points")}
                </p>
              </div>
              {m.my_submission
                ? <StatusBadge status={m.my_submission.status} testId={`mission-status-${m.id}`} />
                : <StatusBadge status="new" testId={`mission-status-${m.id}`} />}
            </div>
            {m.description && <p className="mt-2 line-clamp-2 text-sm text-muted-foreground">{m.description}</p>}
            {m.my_submission?.review_reason && (
              <p className="mt-2 rounded-lg bg-destructive/10 p-2 text-xs text-destructive">
                {m.my_submission.review_reason}
              </p>
            )}
            <p className="mt-3 flex items-center gap-1 text-xs font-bold text-primary"
              data-testid={`mission-open-${m.id}`}>
              <ChevronRight className="h-3.5 w-3.5" />
              {active && part?.checked_in_at && m.my_submission?.status !== "approved"
                ? t("missions.openAndSubmit") : t("missions.viewDetail")}
            </p>
          </button>
        ))}
      </section>

      <MissionDetailSheet mission={activeMission}
        pendingCheckinSync={!part?.checked_in_at && pendingCheckin}
        canSubmit={!!(active && part?.checked_in_at && event.status !== "cancelled"
          && activeMission?.my_submission?.status !== "approved")}
        onClose={() => setActiveMission(null)}
        onDone={async () => { setActiveMission(null); await load(); }} />
    </div>
  );
}
